import { useState } from 'react'

type DeleteAccountCardProps = {
  title: string
  description: string
  warningTitle: string
  warningBody: string
  openModalLabel: string
  modalTitle: string
  modalDescription: string
  passwordLabel: string
  confirmLabel: string
  cancelLabel: string
  submitLabel: string
  submitBusyLabel: string
  submitting: boolean
  onSubmit: (password: string) => void
}

export const DeleteAccountCard = ({
  title,
  description,
  warningTitle,
  warningBody,
  openModalLabel,
  modalTitle,
  modalDescription,
  passwordLabel,
  confirmLabel,
  cancelLabel,
  submitLabel,
  submitBusyLabel,
  submitting,
  onSubmit,
}: DeleteAccountCardProps) => {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const closeModal = () => {
    setOpen(false)
    setPassword('')
    setConfirmed(false)
  }

  const canSubmit = confirmed && password.trim().length >= 8 && !submitting

  return (
    <section className="ca-theme-transition rounded-3xl border border-(--color-status-error-border) bg-(--color-status-error-bg) p-5 shadow-(--shadow-sm)">
      <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-(--color-status-error)">{title}</h2>
      <p className="mt-1 text-sm text-(--color-status-error)">{description}</p>
      <div className="ca-theme-transition mt-4 rounded-xl border border-(--color-status-error-border) bg-(--color-bg-card) px-3 py-2 text-sm text-(--color-status-error)">
        <p className="font-semibold">{warningTitle}</p>
        <p className="mt-1">{warningBody}</p>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ca-theme-transition mt-4 inline-flex h-9 items-center rounded-xl border border-(--color-status-error) bg-(--color-status-error) px-4 text-sm font-semibold text-(--color-text-inverse) hover:bg-[color-mix(in_srgb,var(--color-status-error)_86%,black)]"
      >
        {openModalLabel}
      </button>
      {open ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-(--color-overlay) px-4">
          <div className="ca-card-shell ca-theme-transition w-full max-w-md rounded-2xl p-5 shadow-(--shadow-lg)">
            <h3 className="text-lg font-semibold text-(--color-text-primary)">{modalTitle}</h3>
            <p className="mt-1 text-sm text-(--color-text-secondary)">{modalDescription}</p>
            <label className="mt-4 block text-sm text-(--color-text-secondary)">
              <span className="mb-1 block font-medium">{passwordLabel}</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="ca-input w-full px-3 py-2 text-sm"
              />
            </label>
            <label className="mt-3 flex items-start gap-2 text-sm text-(--color-text-secondary)">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-(--color-border-default) text-(--color-status-error) focus:ring-(--color-focus-ring)"
              />
              <span>{confirmLabel}</span>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="ca-btn-secondary inline-flex h-9 items-center px-4 text-sm font-semibold"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => {
                  onSubmit(password.trim())
                  closeModal()
                }}
                disabled={!canSubmit}
                className="ca-theme-transition inline-flex h-9 items-center rounded-xl border border-(--color-status-error) bg-(--color-status-error) px-4 text-sm font-semibold text-(--color-text-inverse) hover:bg-[color-mix(in_srgb,var(--color-status-error)_86%,black)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? submitBusyLabel : submitLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
